// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { productListLineStyles } from "@steiner/Overrides/Widgets/MyListsDetails/MyListsDetailsProductListLine";
import Check from "@steiner/XDL/Icons/Check";
import Trash from "@steiner/XDL/Icons/Trash";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

productListLineStyles.lineInnerContainer = {
    css: css`
        display: grid;
        grid-template-columns: auto 92px minmax(0, 1fr) 185px 224px;
        gap: 20px;
        align-items: start;
        padding: 12px;
        border-bottom: 1px solid ${color("common.border")};
        width: 100%;

        ${xScreen("lte", "sm")} {
            grid-template-columns: auto 92px minmax(0, 1fr);
            grid-template-rows: auto auto auto;
            height: 100%;
            padding: 12px 0;
        }
        ${xScreen("lte", "xs")} {
            grid-template-columns: 92px minmax(0, 1fr);
            grid-template-rows: auto auto auto auto auto;
            height: 100%;
        }
    `,
};

productListLineStyles.selectAndSort = {
    css: css`
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        ${xScreen("lte", "xs")} {
            flex-direction: row;
            grid-column: 1 / 3;
        }
    `,
};

productListLineStyles.productImageStyles = {
    linkWrappingImage: {
        css: css`
            ${xScreen("lte", "xs")} {
                grid-column: 1 / 2;
                grid-row: 2 / 3;
            }
        `,
    },
    img: {
        css: css`
            max-height: 92px;
        `,
    }
};

productListLineStyles.descriptionAndBasicInfoItem = {
    css: css`
        grid-column: 3 / 4;
        grid-row: 1/4;
        ${xScreen("lte", "sm")} {
            grid-column: 3 / 4;
            grid-row: 1 / 2;
        }
        ${xScreen("lte", "xs")} {
            grid-column: 2 / 3;
            grid-row: 2 / 3;
        }
    `,
};

productListLineStyles.priceAndAvailabilityItem = {
    css: css`
        grid-column: 4 / 5;
        grid-row: 1/4;
        ${xScreen("lte", "sm")} {
            grid-column: 3 / 4;
            grid-row: 2 / 3;
        }
        ${xScreen("lte", "xs")} {
            grid-column: 2 / 3;
            grid-row: 3 / 4;
        }
    `,
};

productListLineStyles.listActionsContainer = {
    css: css`
        grid-column: 5 / 6;
        grid-row: 1 / 4;
        ${xScreen("lte", "sm")} {
            grid-column: 2 / 4;
            grid-row: 3 / 4;
        }
        ${xScreen("lte", "xs")} {
            grid-column: 1 / 3;
            grid-row: 4 / 5;
        }
        display: grid;
        gap: 8px;
        grid-template-columns: auto 1fr;
        grid-template-rows: auto auto;
    `,
};

productListLineStyles.addToCartButton = {
    css: css`
        white-space: nowrap;
    `,
};

productListLineStyles.deleteLink = {
    ...createLinkIconStyles({
        icon: Trash,
        linkStyles: {
            icon: {
                position: "left",
            },
        },
    }),
    css: css`
        margin-top: 10px;
    `,
};

productListLineStyles.tableDeleteLink = {
    icon: {
        iconProps: {
            src: Trash,
            size: 15,
        },
    },

     css: css`
        ${xScreen("lte", "md")} {
            position: absolute;
            bottom: 32px;
            right: 5px;
        }
    `,
};

productListLineStyles.productDescriptionStyles = {
    productDetailDescription: {
        size: fontConfig.fontSize.small,
        lineHeight: fontConfig.lineHeight.small,
        css: css`
            margin-bottom: 8px;
            ${ellipsis(3)}
        `,
    },
    productDetailLink: {
        typographyProps: {
            size: fontConfig.fontSize.small,
            lineHeight: fontConfig.lineHeight.small,
        },
        css: css`
            margin-bottom: 8px;
            ${ellipsis(3)}
        `,
    },
};

productListLineStyles.productPartNumbersStyles = {};

productListLineStyles.notesStyles = {
    wrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    text: {
        size: fontConfig.fontSize.xSmall,
        lineHeight: fontConfig.lineHeight.xSmall,
    },
};

productListLineStyles.editNotesLink = {
    typographyProps: {
        size: fontConfig.fontSize.xSmall,
        lineHeight: fontConfig.lineHeight.xSmall,
        weight: fontConfig.weight.semiBold,
    },
};

productListLineStyles.priceAndAvailabilityInnerContainer = { gap: 8 };

productListLineStyles.productDescriptionDataTableCell = {
    mergeCss: true,
    css: css`
        min-width: 200px;
        max-width: 400px;
        word-wrap: break-word;
        white-space: normal;
        &&& ${TypographyStyle} {
            text-align: left;
            ${ellipsis(1)}
        }
    `,
};

productListLineStyles.productDateDataTableCell = {
    css: css`
        ${xScreen("lte", "md")} {
         p {
            text-align: left;
         }
        }
    `,
};

productListLineStyles.availabilityDataTableCell = {
    mergeCss: true,
    css: css`
        min-width: 230px;
        word-wrap: break-word;
        white-space: normal;
    `,
};

productListLineStyles.selectedCheckbox = {
    css: css`
        margin-right: 0;

        @media print {
            display: none;
        }
    `,
};

productListLineStyles.tableActionsContainer = {
    css: css`
        display: grid;
        grid-template-columns: auto auto;
        gap: 8px;
    `,
};

productListLineStyles.tableUpdateQuantityLinkContainer = {
    css: css`
        grid-column: 1/3;
    `,
};

productListLineStyles.tableQunatityUpdatedTextContainer = {
    css: css`
        grid-column: 1/3;
    `,
};

productListLineStyles.listUpdateQuantityLinkContainer = {
    css: css`
        grid-column: 1/3;
        grid-row: 2 / 4;
    `,
};

productListLineStyles.listQunatityUpdatedTextContainer = {
    css: css`
        grid-column: 1/3;
        grid-row: 3 / 4;
    `,
};

productListLineStyles.quantityUpdatedText = {
    size: fontConfig.fontSize.xSmall,
    lineHeight: fontConfig.lineHeight.xSmall,
};

productListLineStyles.quantityUpdatedIcon = {
    size: 12,
    src: Check,
    css: css`
        margin-right: 8px;
    `,
};

productListLineStyles.restrictedLineContainer = {
    css: css`
        display: grid;
        gap: 20px;
        align-items: start;
        padding: 12px;
        border-bottom: 1px solid ${color("common.border")};
        width: 100%;
        grid-template-rows: auto auto;
        height: 100%;

        ${({ editingSortOrder }: any) =>
            editingSortOrder
                ? css`
                      grid-template-columns: 18px 1fr 224px;
                      ${xScreen("lte", "sm")} {
                          grid-template-columns: 18px 1fr;
                      }
                      ${xScreen("lte", "xs")} {
                          grid-template-rows: auto auto auto;
                      }
                  `
                : css`
                      grid-template-columns: 12px 1fr 224px;
                      ${xScreen("lte", "sm")} {
                          grid-template-columns: 12px 1fr;
                      }
                      ${xScreen("lte", "xs")} {
                          grid-template-rows: auto auto;
                      }
                  `};

        ${xScreen("lte", "sm")} {
            padding: 12px 0;
        }

        ${xScreen("lte", "xs")} {
            grid-template-columns: 1fr;
            height: 100%;
        }
    `,
};

productListLineStyles.restrictedLineInfo = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        grid-column: 2 / 3;
        ${xScreen("lte", "xs")} {
            grid-column: 1 / 2;
            ${({ editingSortOrder }: any) =>
                editingSortOrder
                    ? css`
                          grid-row: 2 / 3;
                      `
                    : css`
                          grid-row: 1 / 2;
                      `}
        }
    `,
};

productListLineStyles.restrictedLineActions = {
    css: css`
        display: flex;
        flex-direction: column;
        align-items: start;
        gap: 8px;
        grid-column: 3 / 4;
        ${xScreen("lte", "sm")} {
            grid-column: 2 / 4;
            grid-row: 2 / 3;
        }
        ${xScreen("lte", "xs")} {
            grid-column: 1 / 2;
            ${({ editingSortOrder }: any) =>
                editingSortOrder
                    ? css`
                          grid-row: 3 / 4;
                      `
                    : css`
                          grid-row: 2 / 3;
                      `}
        }
    `,
};

productListLineStyles.restrictedMessageText = {
    css: css`
        margin-bottom: 0;
    `,
};

productListLineStyles.restrictedDescriptionText = {
    size: fontConfig.fontSize.small,
    lineHeight: fontConfig.lineHeight.small,
    css: css`
        ${ellipsis(3)}
    `,
};

productListLineStyles.restrictedRemoveItemLink = {
    ...createLinkIconStyles({
        icon: Trash,
        linkStyles: {
            icon: {
                position: "left",
            },
            css: css`
                margin-top 20px;
            `,
        },
    }),
};

productListLineStyles.restrictedRemoveItemTableLink = {
    icon: {
        iconProps: {
            src: Trash,
            size: 15,
            css: css`
                margin-right: 0;
            `,
        },
    },
};

productListLineStyles.sortOrderTextField = {
    mergeCss: true,
    cssOverrides: {
        inputSelect: css`
            width: 60px;
        `,
    },
};

productListLineStyles.actionsWrapper = {
    css: css`
        margin-top: 15px;
    `,
}

productListLineStyles.DataTableWrapper = {
    css: css`
        position: relative;
        p[class^="HeaderTypography"] {
            display: none;
        }
    `,
}